import { ErrorMessage } from '@hookform/error-message';
import EmailIcon from '@mui/icons-material/Email';
import { useMediaQuery } from '@mui/material';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import SmallForm from './small-form';
import { useSnackbarContext } from '../base/snackbar';

import Arranger from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import { BrandLogo as BrandLogoLight } from '~c/components/base/icon/asenion-logo';
import { BrandLogo as BrandLogoDark } from '~c/components/base/icon/asenion-logo-dark';
import Link from '~c/components/base/link';
import { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';
import TextField from '~c/components/base/text-field';
import {
  useYupValidationResolver,
  muiError,
} from '~c/hooks/yup-validation-resolver';
import { authApi, nodeAuthApi } from '~c/services';

interface InputData {
  email: string;
  resetResult: string;
}

const validationSchema = yup.object().shape({
  email: yup.string().trim().required(),
});

interface Props {
  className?: string;
}

const ForgotPasswordImpl: React.FC<Props> = ({ className }) => {
  const [passwordReset, { isLoading }] =
    nodeAuthApi.useForgotPasswordMutation();
  const theme = useTheme();
  const prefersDarkMode = theme.palette.mode === 'dark';
  const resolver = useYupValidationResolver(validationSchema);
  const { register, formState, setError, clearErrors, handleSubmit } =
    useForm<InputData>({
      resolver,
    });

  const { errors } = formState;
  const Snackbar = useSnackbarContext();
  const onSubmit = async (input: InputData) => {
    await passwordReset({
      resetEmail: input.email,
    })
      .then((resp: any) => {
        if ('data' in resp && !('error' in resp?.data)) {
          Snackbar.open(resp?.data?.message, {
            showOk: true,
          });
        } else {
          setError('resetResult', {
            message: `${resp.error.data.error}`,
          });
        }
      })
      .catch(() => {
        // handle error
      });
  };

  return (
    <SmallForm
      className={className}
      title={
        <Arranger direction="column" alignItems="center">
          <div className="logo-container">
            {prefersDarkMode ? <BrandLogoLight /> : <BrandLogoDark />}
          </div>
          <Text sz="h1" sx={{ pt: spc.xxxs }}>
            Forgot Password
          </Text>
        </Arranger>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <TextField
          {...register('email')}
          {...muiError(errors, 'email')}
          required
          fullWidth
          label="Email Address"
          autoComplete="email"
          autoFocus
          sx={{ pb: spc.between_vertical_s }}
          startAdornment={<EmailIcon />}
          onChange={() => clearErrors('resetResult')}
        />
        <input {...register('resetResult')} hidden />
        <ErrorMessage
          as={<Text color="error" />}
          errors={errors}
          name="resetResult"
        />
        <Arranger direction="column" alignItems="center">
          <Button
            loading={isLoading}
            type="submit"
            sx={{ mt: spc.xs, mb: spc.between_vertical_s }}
          >
            Reset
          </Button>
        </Arranger>
        <Arranger justify="space-between">
          <Link href="/sign-in" variant="body2">
            Sign In
          </Link>
        </Arranger>
      </form>
    </SmallForm>
  );
};

export const ForgotPassword = muiStyled(ForgotPasswordImpl)(
  ({ theme }) => css`
    .logo-container {
      margin-right: ${spc.l * 2}px;

      svg {
        width: 150px;
        height: 50px;
      }
    }
  `
);

export default ForgotPassword;
